/**
 * The strike ladders for SEVERAL expiries at once — ROADMAP 9.43.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY A SECOND CHAIN HOOK EXISTS
 *
 * `useChainCatalogue` owns ONE expiry: the one the chain table is rendering,
 * which is a selection the trader makes and the Terminal holds. That is the
 * right model for the ladder and the wrong one for a basket, because a leg
 * may now sit on an expiry of its own — which is what a calendar spread IS.
 *
 * So this reads, for an arbitrary set of expiry dates, the ladders already
 * behind `prefetchChain`'s cache and fetches the ones that are missing. It
 * does not own a selection and it does not change one; the Terminal's chain
 * keeps rendering whatever it was rendering.
 *
 * IT IS THE CATALOGUE, NOT THE FEED. These rows say which contracts EXIST
 * and what their tokens are — enough to resolve an ATM offset and enough to
 * send an order. Prices are a separate concern and a separate cost: a leg on
 * a far expiry is subscribed individually by whoever displays it, never by
 * warming a whole second ladder (`demandListener` caps a session at 590).
 *
 * ONE CACHE, SHARED. `prefetchChain` dedupes in flight and `cachedChain`
 * reads the same map `useChainCatalogue` reads, so an expiry the chain table
 * is already showing costs nothing here.
 * ─────────────────────────────────────────────────────────────────────────────
 */

import { useEffect, useMemo, useSyncExternalStore } from 'react'
import {
  cachedChain,
  catalogueRevision,
  prefetchChain,
  subscribeCatalogue,
} from '@/feed/prefetchChain'
import type { PreviewChainRow } from '@/hooks/useStrategyPreviews'

const NO_ROWS: readonly PreviewChainRow[] = []

/** Ladders by expiry date. An expiry still loading is simply absent. */
export type ChainsByExpiry = ReadonlyMap<string, readonly PreviewChainRow[]>

export function useChainsByExpiry(
  underlying: string,
  expiries: readonly string[],
  enabled = true,
): ChainsByExpiry {
  // The cache is not a React value, so its revision is what re-renders us
  // AND what re-runs the memo below when a fetch lands.
  const revision = useSyncExternalStore(subscribeCatalogue, catalogueRevision)

  /*
   * Joined into a key so the effect below fires on the SET changing rather
   * than on every render that rebuilt the array. A caller computing the
   * list inline is the normal case and must not cause a fetch per frame.
   */
  const key = [...new Set(expiries)].sort().join(',')

  useEffect(() => {
    if (!enabled || key === '') return
    for (const expiry of key.split(',')) {
      if (cachedChain(underlying, expiry)) continue
      /*
       * Fire-and-forget with an explicit owner: the cache. A failure leaves
       * the expiry absent from the map, which renders as "still loading"
       * and refuses to place — the same state as a slow network, and the
       * honest one. Nothing here can usefully retry on the trader's behalf.
       */
      void prefetchChain(underlying, expiry).catch(() => undefined)
    }
  }, [underlying, key, enabled])

  return useMemo(() => {
    const out = new Map<string, readonly PreviewChainRow[]>()
    if (key === '') return out
    for (const expiry of key.split(',')) {
      const chain = cachedChain(underlying, expiry)
      if (!chain) continue
      const byStrike = new Map<number, PreviewChainRow>()
      for (const i of chain.items) {
        if (i.strike == null) continue
        const row = byStrike.get(i.strike) ?? { strike: i.strike, ceToken: null, peToken: null }
        const token = i.brokerToken ? Number(i.brokerToken) : null
        if (i.optionType === 'CE') row.ceToken = token
        else if (i.optionType === 'PE') row.peToken = token
        byStrike.set(i.strike, row)
      }
      out.set(expiry, [...byStrike.values()].sort((a, b) => a.strike - b.strike))
    }
    return out
  }, [underlying, key, revision])
}

export const rowsOf = (chains: ChainsByExpiry, expiry: string | null): readonly PreviewChainRow[] =>
  (expiry === null ? undefined : chains.get(expiry)) ?? NO_ROWS
